/**
 * Ліва навігація (UX-02; layout.md, розд. 1 і 3): основні розділи вгорі, «Адміністрування» і
 * перемикач вигляду внизу. Рейка 64 px або розгорнута 224 px — розкладку дає CSS за атрибутом
 * `data-nav` на `.app`; кнопка лише закріплює вибір (nav-mode.ts).
 */
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useMailUnread } from '../features/mail/unread.ts';
import { countText } from '../features/notifications/count.ts';
import { TASK_ENTITY } from '../features/today/source.ts';
import { TaskCountBadge, useTaskCount } from '../features/today/TodayToggle.tsx';
import { shell as t } from '../i18n/uk/shell.ts';
import type { NavMode } from '../prefs/prefs.ts';
import { setPrefs } from '../prefs/prefs.ts';
import { Icon } from '../ui/Icon.tsx';
import { MenuButton } from '../ui/Menu.tsx';
import { NavLink } from './NavLink.tsx';
import { isNavExpanded, toggledNavMode } from './nav-mode.ts';
import { isCurrentNav, MAIL_NAV_ID, type NavEntry, type ShellNav } from './nav-model.ts';
import { useTodayPanel } from './today-context.ts';

export const NAV_ID = 'app-nav';

const isTasks = (item: NavEntry) => item.entity === TASK_ENTITY;
const isMail = (item: NavEntry) => item.id === MAIL_NAV_ID;

/**
 * «Ще» (UX-02): розділи понад перші сім — меню за кнопкою. Відкритий розділ із «Ще» позначено
 * пунктом меню з галочкою, а сама кнопка — як активний пункт навігації.
 */
function MoreNav({ items }: { items: NavEntry[] }) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isCurrent = (item: NavEntry) => isCurrentNav(item, pathname);
  const current = items.some(isCurrent);
  return (
    <MenuButton
      label={t.navMoreLabel}
      align="start"
      className={current ? 'nav__item nav__item--current' : 'nav__item'}
      items={items.map((item) => ({
        kind: 'radio' as const,
        id: item.id,
        label: item.label,
        checked: isCurrent(item),
        onSelect: () => void navigate({ to: item.to }),
      }))}
    >
      <Icon name="more" size={20} />
      <span className="nav__label">{t.navMore}</span>
    </MenuButton>
  );
}

export function SideNav({ nav, mode, wide }: { nav: ShellNav; mode: NavMode; wide: boolean }) {
  const expanded = isNavExpanded(mode, wide);
  // «Задачі N» — лише коли в меню є пункт задач: інакше запиту «Сьогодні» поза стартовим немає.
  const { source } = useTodayPanel();
  const tasks = useTaskCount(nav.items.some(isTasks) ? source : null);
  const unread = useMailUnread(nav.items.some(isMail));
  const toggleLabel = expanded ? t.navCollapse : t.navExpand;
  return (
    <nav id={NAV_ID} className="nav" aria-label={t.navLabel} tabIndex={-1}>
      <ul className="nav__list">
        {nav.items.map((item) => (
          <li key={item.id}>
            <NavLink
              item={item}
              className="nav__item"
              badge={
                isTasks(item) && tasks && tasks.total > 0 ? (
                  <TaskCountBadge count={tasks} />
                ) : isMail(item) && unread > 0 ? (
                  <span className="count count--accent" title={t.navMailUnread(unread)}>
                    <span aria-hidden="true">{countText(unread)}</span>
                    <span className="sr-only">{t.navMailUnread(unread)}</span>
                  </span>
                ) : null
              }
            />
          </li>
        ))}
        {nav.more && nav.more.length > 0 && (
          <li>
            <MoreNav items={nav.more} />
          </li>
        )}
      </ul>
      <div className="nav__foot">
        {nav.admin && (
          <ul className="nav__list">
            <li>
              <NavLink item={nav.admin} className="nav__item" />
            </li>
          </ul>
        )}
        <button
          className="btn btn--ghost btn--icon nav__toggle"
          type="button"
          aria-expanded={expanded}
          aria-controls={NAV_ID}
          aria-label={toggleLabel}
          title={toggleLabel}
          data-testid="nav-toggle"
          onClick={() => setPrefs({ nav: toggledNavMode(mode, wide) })}
        >
          <Icon name={expanded ? 'chevron-left' : 'chevron-right'} />
        </button>
      </div>
    </nav>
  );
}
